import { useContext, useState } from "react";
import {
  hasSessionAssignationChanges,
  sessionAssignationStore,
  useSessionAssignationStore,
} from "@context/sessionAssignation";
import type { AssignmentViewMode } from "@services/types/session-assignation";
import { AppContext } from "../../context/EditSessionsContext";
import { Types as FormTypes } from "../../context/formData.reducer";
import { Types as ResultTypes } from "../../context/result.reducer";
import { Types as PageTypes } from "../../context/switchPage.reducer";
import { hasTraditionalAssignmentChanges } from "../../utils/session-assignation";

export const useAssignmentViewMode = (isLocked: boolean) => {
  const { state, dispatch } = useContext(AppContext);
  const [view, setView] = useState<AssignmentViewMode>("traditional");
  const [pendingView, setPendingView] = useState<AssignmentViewMode | null>(null);
  const phase = useSessionAssignationStore(draft => draft.phase);
  const isBusy = state.page?.loading || ["validating", "committing"].includes(phase);
  const isShowingResults =
    state.page?.active?.results || ["results", "committing", "saved"].includes(phase);
  const disabled = isLocked || isBusy || isShowingResults;

  const switchView = (next: AssignmentViewMode) => {
    if (disabled || next === view) return;
    const threshold = state.form?.blockingThreshold ?? null;
    dispatch({ type: FormTypes.CleanForm, payload: true });
    dispatch({ type: FormTypes.SetBlockingThreshold, payload: threshold });
    dispatch({ type: ResultTypes.CleanResultData });
    dispatch({ type: PageTypes.CleanSwitchPage });
    sessionAssignationStore.getState().reset();
    setPendingView(null);
    setView(next);
  };

  const requestView = (next: AssignmentViewMode) => {
    if (disabled || next === view) return;
    const dirty =
      view === "traditional"
        ? hasTraditionalAssignmentChanges(state)
        : hasSessionAssignationChanges(sessionAssignationStore.getState());
    if (dirty) setPendingView(next);
    else switchView(next);
  };

  return {
    view,
    disabled,
    pendingView,
    requestView,
    cancelSwitch: () => setPendingView(null),
    confirmSwitch: () => pendingView && switchView(pendingView),
  };
};
